import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, FileText, Trash2, Sparkles, Paperclip, AlertTriangle, Receipt, Pencil, Wallet, X } from "lucide-react";
import { api, API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrencyExact, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

export default function Contracts() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [rcAmount, setRcAmount] = useState("");
  const [rcMonth, setRcMonth] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiFile, setAiFile] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const aiRef = useRef(null);
  const currentMonth = new Date().toISOString().slice(0, 7);

  const runSummary = async () => {
    if (!aiFile) return toast.error(t("summarize_contract"));
    setAiBusy(true);
    setSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", aiFile);
      const res = await api.post("/ai/summarize-contract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSummary(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI error");
    } finally {
      setAiBusy(false);
    }
  };

  const { data: items = [] } = useQuery({ queryKey: ["contracts"], queryFn: async () => (await api.get("/contracts")).data });
  const { data: properties = [] } = useQuery({ queryKey: ["properties"], queryFn: async () => (await api.get("/properties")).data });
  const { data: tenants = [] } = useQuery({ queryKey: ["tenants"], queryFn: async () => (await api.get("/tenants")).data });

  const propName = (id) => properties.find((p) => p.id === id)?.name || "—";
  const tenantName = (id) => tenants.find((x) => x.id === id)?.name || "—";

  const create = useMutation({
    mutationFn: (payload) => (editingId ? api.put(`/contracts/${editingId}`, payload) : api.post("/contracts", payload)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false); setForm({}); setEditingId(null); toast.success("OK");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/contracts/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contracts"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
  });
  const closeDeleteDialog = () => { setDeleteTarget(null); setDeleteConfirmText(""); };
  const confirmDelete = () => { remove.mutate(deleteTarget); closeDeleteDialog(); };
  const scheduleChange = useMutation({
    mutationFn: ({ cid, amount, effective_from }) => api.post(`/contracts/${cid}/rent-changes`, { amount, effective_from }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      setRcAmount(""); setRcMonth(""); toast.success("OK");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });
  const deleteChange = useMutation({
    mutationFn: ({ cid, rcid }) => api.delete(`/contracts/${cid}/rent-changes/${rcid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contracts"] }),
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });

  const openCreate = () => { setEditingId(null); setForm({}); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      property_id: c.property_id, tenant_id: c.tenant_id || "", rent_amount: c.rent_amount,
      deposit: c.deposit, start_date: c.start_date || "", end_date: c.end_date || "",
    });
    setOpen(true);
  };
  const editingContract = items.find((c) => c.id === editingId);
  // The earliest entry is the contract's original rent (set at creation, or by
  // the "Valor da renda" field above while unpaid) — everything after that is a
  // recorded change, whether already in effect or still scheduled ahead.
  const sortedChanges = [...(editingContract?.rent_changes || [])].sort((a, b) => a.effective_from.localeCompare(b.effective_from));
  const scheduledChanges = sortedChanges.slice(1);

  const submit = () => {
    if (!form.property_id) return toast.error(t("property"));
    create.mutate({
      property_id: form.property_id,
      tenant_id: form.tenant_id || null,
      rent_amount: parseFloat(form.rent_amount) || 0,
      deposit: parseFloat(form.deposit) || 0,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      status: "active",
    });
  };

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl">{t("nav_contracts")}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setAiOpen(true); setSummary(null); setAiFile(null); }}
            data-testid="ai-summary-btn" className="rounded-sm gap-2">
            <Sparkles className="h-4 w-4" /> <span className="hidden sm:inline">{t("ai_summary")}</span>
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} data-testid="add-contract-btn" className="rounded-sm gap-2">
                <Plus className="h-4 w-4" /> <span className="hidden sm:inline">{t("add_contract")}</span>
              </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? t("edit_contract") : t("add_contract")}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2 space-y-2"><Label>{t("property")}</Label>
                <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                  <SelectTrigger data-testid="contract-property" className="rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-popover">{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="col-span-2 space-y-2"><Label>{t("tenant")}</Label>
                <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                  <SelectTrigger data-testid="contract-tenant" className="rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-popover">{tenants.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2">
                <Label>{t("rent_amount")}</Label>
                <Input type="number" data-testid="contract-rent" value={form.rent_amount || ""}
                  disabled={!!editingContract?.has_payments}
                  onChange={(e) => setForm({ ...form, rent_amount: e.target.value })} className="rounded-sm" />
                {editingContract?.has_payments && (
                  <p className="text-xs text-muted-foreground">{t("rent_locked_hint")}</p>
                )}
              </div>
              <div className="space-y-2"><Label>{t("deposit")}</Label>
                <Input type="number" data-testid="contract-deposit" value={form.deposit || ""} onChange={(e) => setForm({ ...form, deposit: e.target.value })} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>{t("start_date")}</Label>
                <Input type="date" data-testid="contract-start" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>{t("end_date")}</Label>
                <Input type="date" data-testid="contract-end" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm" /></div>
            </div>

            {editingId && (
              <div className="border-t border-border pt-4 mt-2 space-y-3">
                <Label className="text-sm">{t("schedule_rent_change")}</Label>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">{t("rent_amount")}</Label>
                    <Input type="number" data-testid="rc-amount" value={rcAmount}
                      onChange={(e) => setRcAmount(e.target.value)} className="rounded-sm w-32" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t("effective_from")}</Label>
                    <Input type="month" data-testid="rc-month" value={rcMonth}
                      onChange={(e) => setRcMonth(e.target.value)} className="rounded-sm w-36" />
                  </div>
                  <Button type="button" size="sm" data-testid="rc-schedule-btn"
                    disabled={!rcAmount || !rcMonth || scheduleChange.isPending}
                    onClick={() => scheduleChange.mutate({ cid: editingId, amount: parseFloat(rcAmount), effective_from: rcMonth })}
                    className="rounded-sm">
                    {t("save")}
                  </Button>
                </div>
                {/* Past dates are accepted on purpose — the increase may already
                    have been communicated to the tenant before being recorded
                    here; only months already marked paid are protected. */}
                {scheduledChanges.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t("no_scheduled_changes")}</p>
                ) : (
                  <ul className="space-y-1">
                    {scheduledChanges.map((rc) => (
                      <li key={rc.id} data-testid={`rc-row-${rc.id}`} className="flex items-center justify-between text-sm bg-secondary/50 rounded-sm px-3 py-1.5">
                        <span>
                          {rc.effective_from} — {fmtCurrencyExact(rc.amount)}
                          {rc.effective_from <= currentMonth && (
                            <Badge variant="outline" className="ml-2 rounded-sm text-[10px] align-middle">{t("rent_change_applied")}</Badge>
                          )}
                        </span>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6"
                          onClick={() => deleteChange.mutate({ cid: editingId, rcid: rc.id })} data-testid={`rc-del-${rc.id}`}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">{t("cancel")}</Button>
              <Button onClick={submit} data-testid="contract-save" disabled={create.isPending} className="rounded-sm">{t("save")}</Button>
            </DialogFooter>
          </DialogContent>
          </Dialog>

          <Dialog open={aiOpen} onOpenChange={setAiOpen}>
            <DialogContent className="bg-popover max-w-lg">
              <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="h-5 w-5 text-[hsl(var(--success))]" /> {t("summarize_contract")}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <p className="text-xs text-muted-foreground">{t("upload_contract_hint")}</p>
                <input ref={aiRef} type="file" accept=".pdf,image/*" hidden data-testid="ai-file-input"
                  onChange={(e) => setAiFile(e.target.files?.[0] || null)} />
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => aiRef.current?.click()} data-testid="ai-attach-btn" className="rounded-sm gap-2">
                    <Paperclip className="h-4 w-4" /> {t("attach_invoice")}
                  </Button>
                  {aiFile && <span className="text-xs text-muted-foreground truncate max-w-[160px]">{aiFile.name}</span>}
                  <Button type="button" size="sm" onClick={runSummary} disabled={!aiFile || aiBusy} data-testid="ai-run-btn" className="rounded-sm gap-2">
                    <Sparkles className="h-4 w-4" /> {aiBusy ? t("analyzing") : t("ai_summary")}
                  </Button>
                </div>

                {summary && (
                  <div className="space-y-4 max-h-[50vh] overflow-y-auto pt-2" data-testid="ai-summary-result">
                    <p className="text-sm">{summary.summary}</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {summary.landlord ? <div><span className="text-muted-foreground text-xs uppercase">{t("parties")}</span><p>{summary.landlord} → {summary.tenant}</p></div> : null}
                      {summary.monthly_rent ? <div><span className="text-muted-foreground text-xs uppercase">{t("rent_amount")}</span><p className="tabular-nums">{summary.monthly_rent} EUR</p></div> : null}
                      {summary.duration ? <div><span className="text-muted-foreground text-xs uppercase">{t("duration")}</span><p>{summary.duration}</p></div> : null}
                    </div>
                    {Array.isArray(summary.key_points) && summary.key_points.length > 0 && (
                      <div>
                        <span className="text-muted-foreground text-xs uppercase">{t("key_points")}</span>
                        <ul className="mt-1 space-y-1">
                          {summary.key_points.map((k, i) => <li key={i} className="text-sm flex gap-2"><span className="text-[hsl(var(--success))]">•</span>{k}</li>)}
                        </ul>
                      </div>
                    )}
                    {Array.isArray(summary.risks) && summary.risks.length > 0 && (
                      <div>
                        <span className="text-[hsl(var(--warning))] text-xs uppercase flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {t("risks")}</span>
                        <ul className="mt-1 space-y-1">
                          {summary.risks.map((r, i) => <li key={i} className="text-sm flex gap-2 text-muted-foreground"><span className="text-[hsl(var(--warning))]">•</span>{r}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {items.length === 0 ? (
        <Card className="p-16 rounded-sm border-dashed border-border text-center">
          <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("empty_generic")}</p>
        </Card>
      ) : (
        <>
          <Card className="hidden sm:block rounded-sm border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("property")}</TableHead>
                  <TableHead>{t("tenant")}</TableHead>
                  <TableHead className="text-right">{t("rent_amount")}</TableHead>
                  <TableHead>{t("start_date")}</TableHead>
                  <TableHead>{t("end_date")}</TableHead>
                  <TableHead>{t("status")}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`contract-row-${c.id}`}>
                    <TableCell className="font-medium">{propName(c.property_id)}</TableCell>
                    <TableCell>{tenantName(c.tenant_id)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtCurrencyExact(c.rent_amount)}</TableCell>
                    <TableCell>{fmtDate(c.start_date)}</TableCell>
                    <TableCell>{fmtDate(c.end_date)}</TableCell>
                    <TableCell><Badge className="rounded-sm bg-[hsl(var(--success))] text-white">{c.status}</Badge></TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" data-testid={`edit-contract-${c.id}`} title={t("edit_contract")}
                        onClick={() => openEdit(c)}
                        className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" asChild data-testid={`payments-link-${c.id}`} title={t("nav_payments")}
                        className="text-muted-foreground hover:text-foreground">
                        <Link to={`/payments/${c.id}`}><Wallet className="h-4 w-4" /></Link>
                      </Button>
                      <Button variant="ghost" size="icon" data-testid={`recibo-${c.id}`} title={t("recibo_worksheet")}
                        onClick={() => window.open(`${API}/contracts/${c.id}/recibo-worksheet`, "_blank")}
                        className="text-muted-foreground hover:text-foreground"><Receipt className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" data-testid={`del-contract-${c.id}`} onClick={() => setDeleteTarget(c.id)}
                        className="text-muted-foreground hover:text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="sm:hidden grid grid-cols-1 gap-4">
            {items.map((c) => (
              <Card key={c.id} data-testid={`contract-card-${c.id}`} className="p-4 rounded-sm border-border">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-display text-lg truncate">{propName(c.property_id)}</h3>
                    <p className="text-sm text-muted-foreground truncate">{tenantName(c.tenant_id)}</p>
                  </div>
                  <Badge className="rounded-sm bg-[hsl(var(--success))] text-white shrink-0">{c.status}</Badge>
                </div>
                <div className="flex items-center justify-between mt-3 text-sm">
                  <span className="text-muted-foreground">{t("rent_amount")}</span>
                  <span className="tabular-nums font-medium">{fmtCurrencyExact(c.rent_amount)}</span>
                </div>
                <div className="flex items-center justify-between mt-1 text-sm">
                  <span className="text-muted-foreground">{t("start_date")}</span>
                  <span>{fmtDate(c.start_date)}</span>
                </div>
                <div className="flex items-center justify-between mt-1 text-sm">
                  <span className="text-muted-foreground">{t("end_date")}</span>
                  <span>{fmtDate(c.end_date)}</span>
                </div>
                <div className="flex items-center justify-end gap-1 mt-4 pt-3 border-t border-border">
                  <Button variant="ghost" size="icon" data-testid={`edit-contract-mobile-${c.id}`} title={t("edit_contract")}
                    onClick={() => openEdit(c)}
                    className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" asChild data-testid={`payments-link-mobile-${c.id}`} title={t("nav_payments")}
                    className="text-muted-foreground hover:text-foreground">
                    <Link to={`/payments/${c.id}`}><Wallet className="h-4 w-4" /></Link>
                  </Button>
                  <Button variant="ghost" size="icon" data-testid={`recibo-mobile-${c.id}`} title={t("recibo_worksheet")}
                    onClick={() => window.open(`${API}/contracts/${c.id}/recibo-worksheet`, "_blank")}
                    className="text-muted-foreground hover:text-foreground"><Receipt className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`del-contract-mobile-${c.id}`} onClick={() => setDeleteTarget(c.id)}
                    className="text-muted-foreground hover:text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) closeDeleteDialog(); }}>
        <DialogContent className="bg-popover">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[hsl(var(--destructive))]">{t("delete_contract_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground py-2">{t("delete_contract_confirm")}</p>
          <Input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="DELETE" data-testid="delete-contract-confirm-input" className="rounded-sm" />
          <DialogFooter>
            <Button variant="ghost" onClick={closeDeleteDialog} className="rounded-sm">{t("cancel")}</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="confirm-delete-contract-btn"
              disabled={deleteConfirmText.trim().toLowerCase() !== "delete"} className="rounded-sm">
              {t("delete_contract_title")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
